import { useEffect, useMemo, useState } from "react";
import { Avatar, Card, Empty, Flex, Select, Skeleton, Typography } from "antd";

import { DynamicOfSalesChart } from "@src/components";
import { CustomResult } from "@repo/ui";
import { buildProductImageUrl } from "@src/components/merchant_products/utils";
import { useMerchantProducts, useMerchants } from "@src/queries";
import type { MerchantProductType } from "@src/types";

const PRODUCT_SEARCH_DEBOUNCE_MS = 350;
const PRODUCT_OPTIONS_PAGE_SIZE = 100;

const { Title, Paragraph, Text } = Typography;

function MerchantProductOption({ product }: { product: MerchantProductType }) {
    const title = product.title || product.product?.title || "Без названия";
    const sku = product.sku || "—";
    const imageSrc = buildProductImageUrl(product.product?.images?.[0]);

    return (
        <div className="flex min-w-0 items-center gap-3">
            <Avatar
                shape="square"
                size={40}
                src={imageSrc}
                className="shrink-0 rounded-lg bg-gray-100 text-sm text-gray-500"
            >
                {title.charAt(0).toUpperCase()}
            </Avatar>

            <div className="min-w-0">
                <div className="truncate text-sm font-medium text-slate-900">{title}</div>
                <div className="truncate text-xs text-slate-500">SKU: {sku}</div>
            </div>
        </div>
    );
}

export function ProductDashboardsPage() {
    const merchantsQuery = useMerchants();
    const merchants = merchantsQuery.data?.data ?? [];

    const [selectedMerchantId, setSelectedMerchantId] = useState<string>();
    const [selectedMerchantProductId, setSelectedMerchantProductId] = useState<string>();
    const [productSearchInput, setProductSearchInput] = useState("");
    const [debouncedProductSearch, setDebouncedProductSearch] = useState("");

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedProductSearch(productSearchInput.trim());
        }, PRODUCT_SEARCH_DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutId);
    }, [productSearchInput]);

    useEffect(() => {
        if (!merchants.length) {
            setSelectedMerchantId(undefined);
            return;
        }

        setSelectedMerchantId((current) => {
            if (current && merchants.some((merchant) => merchant.id === current)) {
                return current;
            }

            return merchants[0]?.id;
        });
    }, [merchants]);

    useEffect(() => {
        setSelectedMerchantProductId(undefined);
        setProductSearchInput("");
        setDebouncedProductSearch("");
    }, [selectedMerchantId]);

    const merchantProductsQuery = useMerchantProducts({
        merchantId: selectedMerchantId,
        page: 1,
        size: PRODUCT_OPTIONS_PAGE_SIZE,
        search: debouncedProductSearch || null,
        enabled: Boolean(selectedMerchantId),
    });

    const merchantProducts = merchantProductsQuery.data?.data ?? [];

    const merchantOptions = useMemo(
        () =>
            merchants.map((merchant) => ({
                value: merchant.id,
                label: merchant.store_name || merchant.merchant_uid,
            })),
        [merchants],
    );

    const merchantProductOptions = useMemo(
        () =>
            merchantProducts.map((merchantProduct) => ({
                value: merchantProduct.id,
                label: <MerchantProductOption product={merchantProduct} />,
            })),
        [merchantProducts],
    );

    const selectedMerchant = useMemo(
        () => merchants.find((merchant) => merchant.id === selectedMerchantId),
        [merchants, selectedMerchantId],
    );

    const selectedMerchantProduct = useMemo(
        () =>
            merchantProducts.find(
                (merchantProduct) => merchantProduct.id === selectedMerchantProductId,
            ),
        [merchantProducts, selectedMerchantProductId],
    );

    if (merchantsQuery.isError) {
        return (
            <CustomResult
                type="error"
                title="Не удалось загрузить merchants"
                axiosError={merchantsQuery.error}
                withCard
            />
        );
    }

    return (
        <Flex vertical gap={24} style={{ width: "100%" }}>
            <div>
                <Title level={2} style={{ margin: 0 }}>
                    Product dashboards
                </Title>
                <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                    Сначала выберите магазин пользователя и merchant product. Открытие самого
                    dashboard по выбранному товару добавим следующим шагом.
                </Paragraph>
            </div>

            <Card className="border border-slate-200 shadow-sm">
                {merchantsQuery.isLoading ? (
                    <Skeleton active paragraph={{ rows: 3 }} />
                ) : merchants.length === 0 ? (
                    <Empty
                        description="У пользователя пока нет доступных merchants"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                ) : (
                    <Flex vertical gap={20}>
                        <div className="grid gap-4 xl:grid-cols-[260px_minmax(0,460px)]">
                            <div className="space-y-2">
                                <Text className="block text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                                    Merchant
                                </Text>
                                <Select
                                    size="middle"
                                    value={selectedMerchantId}
                                    options={merchantOptions}
                                    onChange={setSelectedMerchantId}
                                    showSearch
                                    optionFilterProp="label"
                                    loading={merchantsQuery.isFetching}
                                    placeholder="Выберите merchant"
                                    className="w-full"
                                />
                            </div>

                            <div className="space-y-2">
                                <Text className="block text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                                    Merchant product
                                </Text>
                                <Select
                                    key={selectedMerchantId}
                                    size="middle"
                                    value={selectedMerchantProductId}
                                    options={merchantProductOptions}
                                    onChange={setSelectedMerchantProductId}
                                    onSearch={setProductSearchInput}
                                    showSearch
                                    filterOption={false}
                                    loading={merchantProductsQuery.isFetching}
                                    disabled={!selectedMerchantId}
                                    placeholder="Выберите merchant product"
                                    notFoundContent={
                                        merchantProductsQuery.isFetching
                                            ? "Загружаем товары..."
                                            : debouncedProductSearch
                                              ? "По вашему запросу ничего не найдено"
                                              : "Для этого merchant товары не найдены"
                                    }
                                    className="w-full"
                                />
                            </div>
                        </div>

                        {merchantProductsQuery.isError && (
                            <CustomResult
                                type="error"
                                title="Не удалось загрузить merchant products"
                                axiosError={merchantProductsQuery.error}
                                withCard
                            />
                        )}
                    </Flex>
                )}
            </Card>

            <Card className="border border-dashed border-slate-300 bg-slate-50/70 shadow-none">
                <Flex vertical gap={10}>
                    <Text className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                        Текущий контекст
                    </Text>

                    <div className="space-y-2">
                        <div className="text-sm text-slate-600">
                            Merchant:{" "}
                            <span className="font-medium text-slate-900">
                                {selectedMerchant?.store_name ||
                                    selectedMerchant?.merchant_uid ||
                                    "Не выбран"}
                            </span>
                        </div>
                        <div className="text-sm text-slate-600">
                            Merchant product:{" "}
                            <span className="font-medium text-slate-900">
                                {selectedMerchantProduct?.title ||
                                    selectedMerchantProduct?.product?.title ||
                                    selectedMerchantProduct?.sku ||
                                    "Не выбран"}
                            </span>
                        </div>
                    </div>

                    {!selectedMerchantProductId && (
                        <Text type="secondary">
                            Выберите merchant product, чтобы на следующем шаге открыть его
                            dashboard.
                        </Text>
                    )}
                </Flex>
            </Card>

            {selectedMerchantProductId && (
                <DynamicOfSalesChart merchantProductId={selectedMerchantProductId} />
            )}
        </Flex>
    );
}
